// src/app/collections/auth/price-checker/PriceChecker.tsx
import { Button, Col, Notification, Row, Spacer, Spin, Text } from '@app/components'
import { toCurrency } from '@app/utils'
import axios from 'axios'
import React, { useMemo, useState } from 'react'

// Optional: bring your app’s styles for the left column if helpful
// import { SomeStyledBox } from '../elements'

type TokenKey = 'PLS' | 'WAIT' | 'HEX' | 'PLSX' | 'INC' | 'PHLPv2' | 'TIDE' | 'GLORY' | 'CST'

type PriceCheckerProps = {
  /** Percentage fee applied before quoting (same logic as Buy Summary). Defaults to 0. */
  feePercent?: number
  /** Extra 0.25% you subtract in your Buy Summary formula. Defaults to 0.25. */
  extraHaircutPercent?: number
}

export const PriceChecker: React.FC<PriceCheckerProps> = ({ feePercent = 0, extraHaircutPercent = 0.25 }) => {
  // UI state
  const [amountUsd, setAmountUsd] = useState<string>('100')
  const [token, setToken] = useState<TokenKey>('PLS')
  const [loading, setLoading] = useState(false)
  const [resultAmount, setResultAmount] = useState<number | null>(null)
  const [resultSymbol, setResultSymbol] = useState<string>('')

  const tokenOutAddress = useMemo(() => {
    // mirrors your Buy Summary mapping
    const map: Record<TokenKey, string> = {
      PLS: 'PLS',
      WAIT: '0x26179a4d4b58b4456f28d19507546596c9058ee5',
      HEX: '0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39',
      PLSX: '0x95B303987A60C71504D99Aa1b13B4DA07b0790ab',
      INC: '0x2fa878Ab3F87CC1C9737Fc071108F904c0B0C95d',
      PHLPv2: '0x7eE9946b082E197652Bf5d4Bdc2A034DBfF4121b',
      TIDE: '0xdedBCc8D9458B2556375680FbA297C6aCa6c7DCf',
      GLORY: '0x37D16BabDce0427e63233b3d9be149B31ac2E6f8',
      CST: '0x600136dA8cc6D1Ea07449514604dc4ab7098dB82',
    }
    return map[token]
  }, [token])

  const parsedUsd = useMemo(() => {
    const n = Number(amountUsd)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }, [amountUsd])

  const netAmount6Decimals = useMemo(() => {
    // mimics: pay.amount * ((100 - fee - 0.25) / 100) * 10 ** 6
    const pct = (100 - feePercent - extraHaircutPercent) / 100
    return Math.floor(parsedUsd * pct * 10 ** 6)
  }, [parsedUsd, feePercent, extraHaircutPercent])

  const quote = async () => {
    try {
      setLoading(true)
      setResultAmount(null)

      // CST on PulseChain (same as your Summary)
      const tokenInAddress = '0x600136dA8cc6D1Ea07449514604dc4ab7098dB82'
      const chainId = 369

      if (!parsedUsd || parsedUsd <= 0) {
        Notification({ message: 'Enter a USD amount greater than 0', type: 'error' })
        setLoading(false)
        return
      }

      const url = `/api/piteas/quote?tokenInAddress=${tokenInAddress}&tokenInChainId=${chainId}&tokenOutAddress=${tokenOutAddress}&tokenOutChainId=${chainId}&amount=${netAmount6Decimals}&allowedSlippage=2.00`

      const res = await axios.get(url)
      const data = res.data

      const destDecimals = Number(data?.destToken?.decimals ?? 18)
      const readable = Number(data?.destAmount ?? 0) / 10 ** destDecimals

      setResultAmount(readable)
      setResultSymbol(token)
    } catch (error: any) {
      const msg = error?.message === 'Network Error' ? 'Network Error' : 'Quote error. Try a different amount.'
      Notification({ message: msg, type: 'error' })
      setResultAmount(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ width: '100%', padding: 24 }}>
      <Text style={{ fontSize: 24, fontWeight: 600 }}>Quick Price Check</Text>
      <Spacer value={12} />
      <Text>Check approximate output for a USD amount (no sign-in required).</Text>

      <Spacer value={24} />

      <Row gutter={[12, 12]}>
        <Col span={14}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>USD Amount</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={amountUsd}
            onChange={(e) => setAmountUsd(e.target.value)}
            style={{
              width: '100%',
              height: 40,
              padding: '0 12px',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              outline: 'none',
            }}
            placeholder="e.g. 100"
          />
        </Col>
        <Col span={10}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Asset</label>
          <select
            value={token}
            onChange={(e) => setToken(e.target.value as TokenKey)}
            style={{
              width: '100%',
              height: 40,
              padding: '0 12px',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              outline: 'none',
              background: 'white',
            }}
          >
            <option value="PLS">PLS</option>
            <option value="WAIT">WAIT</option>
            <option value="HEX">HEX</option>
            <option value="PLSX">PLSX</option>
            <option value="INC">INC</option>
            <option value="PHLPv2">PHLPv2</option>
            <option value="TIDE">TIDE</option>
            <option value="GLORY">GLORY</option>
            <option value="CST">CST</option>
          </select>
        </Col>
      </Row>

      <Spacer value={16} />

      <Button block onClick={quote} disabled={loading || !parsedUsd || parsedUsd <= 0} loading={loading}>
        Check Price
      </Button>

      <Spacer value={16} />

      {loading && (
        <Row>
          <Col span={24}>
            <Spin />
          </Col>
        </Row>
      )}

      {!loading && resultAmount !== null && (
        <Row>
          <Col span={24}>
            <Text style={{ fontSize: 16 }}>
              ~{toCurrency(resultAmount, false)} {resultSymbol}
            </Text>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default PriceChecker
